import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { userManager, type UserState } from '@/services/userManager'
import { logger } from '@/utils/logger'

export interface AdminPermissions {
  can_delete_all_slots?: boolean
  can_view_all_data?: boolean
  can_export_data?: boolean
  can_manage_admins?: boolean
}

export interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signOut: () => Promise<void>
  isAuthenticated: boolean
  isAdmin: boolean
  adminPermissions: AdminPermissions
  checkAdminStatus: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [userState, setUserState] = useState<UserState>(userManager.getState())

  useEffect(() => {
    logger.info('AuthProvider initializing with UserManager', { component: 'AuthContext' })

    const unsubscribe = userManager.subscribe((newState) => {
      logger.debug('UserManager state updated', {
        component: 'AuthContext',
        data: { 
          email: newState.user?.email,
          isAdmin: newState.isAdmin,
          isAuthenticated: newState.isAuthenticated
        }
      })
      setUserState(newState)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_UP' as AuthChangeEvent && session?.user) {
        logger.info('Creating user profile for new signup', { 
          component: 'AuthContext',
          user: session.user.email 
        })
        await createUserProfile(session.user)
      }
    })

    return () => {
      unsubscribe()
      subscription.unsubscribe()
    }
  }, [])

  const createUserProfile = async (user: any) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .insert([
          {
            id: user.id,
            email: user.email,
            first_name: user.user_metadata?.first_name || '',
            last_name: user.user_metadata?.last_name || '',
            full_name: user.user_metadata?.full_name || user.user_metadata?.name || '',
            avatar_url: user.user_metadata?.avatar_url || '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ])
        .select()
        .single()

      if (error && error.code !== '23505') {
        logger.error('Error creating user profile', {
          component: 'AuthContext',
          data: error
        })
      }
    } catch (error) {
      logger.error('Error creating user profile', {
        component: 'AuthContext', 
        data: error
      })
    }
  }

  const signOut = async () => {
    const result = await userManager.signOut()
    if (!result.success) {
      logger.error('Sign out failed in AuthContext', {
        component: 'AuthContext',
        data: { error: result.error }
      })
    }
  }

  const value: AuthContextType = {
    user: userState.user,
    session: userState.session,
    loading: userState.loading,
    signOut,
    isAuthenticated: userState.isAuthenticated,
    isAdmin: userState.isAdmin,
    adminPermissions: userState.adminPermissions,
    checkAdminStatus: userManager.refreshAdminStatus.bind(userManager)
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
